import {
  Button,
  Center,
  HStack,
  Icon,
  Image,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
  VStack,
  useToast,
} from "@chakra-ui/react";
import {
  Certificate,
  CheckCircle,
  Clock,
  FileArrowDown,
  Lock,
  Play,
} from "@phosphor-icons/react";
import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Course, Silabus } from "../../const/types";
import formatDate from "../../lib/formatDate";
import formatDuration from "../../lib/formatDuration";
import formatDurationShort from "../../lib/FormatDurationShort";
import NoData from "../feedback/NoData";
import ActiveSilabusWatch from "./ActiveSilabusWatch";

type Props = {
  course: Course;
};

export default function SilabusWatch({ course }: Props) {
  const data = course.subs;
  const location = useLocation();
  const { course_id } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const index = queryParams.get("index");
  const [activeSilabus, setActiveSilabus] = useState<Silabus>(
    data[parseInt(index as string)]
  );
  const navigate = useNavigate();
  const onChangeSilabus = useCallback(
    (index: number) => {
      if (course_id) {
        navigate(`/mycourse/${course_id}?index=${index}`);
      }
    },
    [navigate, course_id]
  );

  useEffect(() => {
    if (index) {
      setActiveSilabus(data[parseInt(index)]);
      onChangeSilabus(parseInt(index));
    }
  }, [index, data, onChangeSilabus]);

  const toast = useToast();
  const isCourseFinished = course?.is_course_finished;

  const [loadingMateri, setLoadingMateri] = useState<boolean>(false);
  const downloadMateri = () => {
    setLoadingMateri(true);
    const url = `${process.env.REACT_APP_WEB_API_BASE_URL}api/course/${course.id}/downloadmateri`;
    axios
      .get(url, {
        responseType: "blob",
        withCredentials: true,
        xsrfCookieName: "XSRF-TOKEN",
        xsrfHeaderName: "X-XSRF-TOKEN",
      })
      .then((r) => {
        if (r.status === 200) {
          const downloadUrl = window.URL.createObjectURL(new Blob([r.data]));
          const link = document.createElement("a");
          link.href = downloadUrl;
          link.setAttribute(
            "download",
            `Materi ${course.nama_course}.${r.data.type.split("/").pop()}`
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(downloadUrl);
        }
      })
      .catch((e) => {
        console.log(e);
        toast({
          title: "Gagal mengunduh",
          description: e?.response?.data?.message,
          status: "error",
          duration: 10000,
          isClosable: true,
        });
      })
      .finally(() => {
        setLoadingMateri(false);
      });
  };

  const [loadingSertifikat, setLoadingSertifikat] = useState<boolean>(false);
  const downloadSertifikat = () => {
    setLoadingSertifikat(true);
    const url = `${process.env.REACT_APP_WEB_API_BASE_URL}api/course/${course.id}/getusercertificate`;
    axios
      .get(url, {
        responseType: "blob",
        withCredentials: true,
        xsrfCookieName: "XSRF-TOKEN",
        xsrfHeaderName: "X-XSRF-TOKEN",
      })
      .then((r) => {
        if (r.status === 200) {
          const downloadUrl = window.URL.createObjectURL(new Blob([r.data]));
          const link = document.createElement("a");
          link.href = downloadUrl;
          link.setAttribute(
            "download",
            `Materi ${course.nama_course}.${r.data.type.split("/").pop()}`
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(downloadUrl);
        }
      })
      .catch((e) => {
        console.log(e);
        toast({
          title: "Gagal mengunduh",
          description: e?.response?.data?.message,
          status: "error",
          duration: 10000,
          isClosable: true,
        });
      })
      .finally(() => {
        setLoadingSertifikat(false);
      });
  };

  return data.length > 0 ? (
    <>
      <Stack
        flexDir={["column", null, null, "row"]}
        gap={5}
        h={"max-content"}
        align={"stretch"}
      >
        <ActiveSilabusWatch
          data={activeSilabus}
          nextIndex={parseInt(index as string) + 1}
          maxIndex={data.length}
        />

        <VStack
          w={["100%", null, null, "30%"]}
          borderRadius={12}
          border={"1px solid var(--divider3)"}
          align={"stretch"}
          gap={0}
          h={"100%"}
          maxH={"473.85px"}
        >
          <Text fontWeight={500} py={2} px={4}>
            Kurikulum
          </Text>

          <VStack
            align={"stretch"}
            overflowY={"auto"}
            flex={1}
            maxH={"473.85px"}
            h={"100%"}
            px={2}
            pb={2}
          >
            {data.map((silabus, i) => {
              const silabusIcon =
                activeSilabus.id === silabus.id ? (
                  <Image
                    src={`${process.env.REACT_APP_MEDIA_BASE_URL}${silabus.thumbnail}`}
                    boxSize={"20px"}
                    borderRadius={"full"}
                    objectFit={"cover"}
                  />
                ) : silabus.playback?.locked ? (
                  <Icon as={Lock} fontSize={20} weight="fill" />
                ) : silabus.playback?.finished ? (
                  <Icon
                    as={CheckCircle}
                    fontSize={28}
                    weight="fill"
                    color={"p.500"}
                  />
                ) : (
                  <Icon as={Play} fontSize={20} weight="fill" />
                );

              const silabusIconAnimation =
                activeSilabus.id === silabus.id
                  ? "infinite-rotate 5s infinite linear"
                  : "";
              const silabusIconBg =
                activeSilabus.id === silabus.id
                  ? "var(--dark)"
                  : "var(--divider3)";
              const silabusBg =
                activeSilabus.id === silabus.id ? "var(--p500a)" : "";
              const silabusOpacity =
                activeSilabus.id === silabus.id || !silabus.playback?.locked
                  ? 1
                  : 0.5;

              return (
                <HStack
                  key={i}
                  h={"50px"}
                  p={2}
                  borderRadius={8}
                  bg={silabusBg}
                  _hover={{ bg: "var(--divider)", opacity: 1 }}
                  cursor={"pointer"}
                  gap={2}
                  opacity={silabusOpacity}
                  onClick={() => {
                    if (!silabus.playback?.locked) {
                      setActiveSilabus(silabus);
                      onChangeSilabus(i);
                    }
                  }}
                >
                  <Center
                    p={1}
                    bg={silabusIconBg}
                    borderRadius={"full"}
                    animation={silabusIconAnimation}
                    flexShrink={0}
                    w={"36px"}
                    h={"36px"}
                  >
                    {silabusIcon}
                  </Center>

                  <Tooltip label={silabus.nama_sub_course} openDelay={500}>
                    <Text noOfLines={1}>{silabus.nama_sub_course}</Text>
                  </Tooltip>
                  <Text ml={"auto"}>
                    {formatDurationShort(silabus.duration)}
                  </Text>
                </HStack>
              );
            })}
          </VStack>

          <SimpleGrid p={4} columns={[1]} gap={2}>
            <Tooltip
              label={
                isCourseFinished
                  ? ""
                  : "Unduh materi bisa dilakukan setelah menyelesaikan course"
              }
            >
              <Button
                isDisabled={!isCourseFinished}
                minH={"50px"}
                leftIcon={<Icon as={FileArrowDown} fontSize={20} />}
                onClick={downloadMateri}
                className="btn-solid clicky"
                isLoading={loadingMateri}
                // w={["100%", null, "50%"]}
              >
                Unduh Materi
              </Button>
            </Tooltip>

            <Tooltip
              label={
                isCourseFinished
                  ? ""
                  : "Unduh sertifikat bisa dilakukan setelah menyelesaikan course"
              }
            >
              <Button
                isDisabled={!isCourseFinished}
                colorScheme="ap"
                minH={"50px"}
                leftIcon={<Icon as={Certificate} fontSize={20} />}
                className="clicky"
                onClick={downloadSertifikat}
                isLoading={loadingSertifikat}
                // w={["100%", null, "50%"]}
              >
                Unduh Sertifikat
              </Button>
            </Tooltip>
          </SimpleGrid>
        </VStack>
      </Stack>

      <VStack
        w={"100%"}
        align={"stretch"}
        gap={0}
        border={"1px solid var(--divider3)"}
        p={4}
        borderRadius={12}
        mt={5}
      >
        <Text fontWeight={600} fontSize={20} mb={1}>
          {activeSilabus.nama_sub_course}
        </Text>
        {/* <Text opacity={0.5} mb={4}>
          {activeSilabus.description}
        </Text> */}
        <HStack mb={1}>
          <Icon as={Clock} fontSize={20} />
          <Text>{formatDuration(activeSilabus.duration)}</Text>
        </HStack>
        <Text>Terakhir diperbarui {formatDate(activeSilabus.updated_at)}</Text>
      </VStack>
    </>
  ) : (
    <NoData />
  );
}
